"use strict";
import { InterfaceEventName } from "@uniswap/analytics-events";
import DefaultMenu from "components/AccountDrawer/DefaultMenu";
import { useAccountDrawer } from "components/AccountDrawer/MiniPortfolio/hooks";
import useDisableScrolling from "hooks/useDisableScrolling";
import usePrevious from "hooks/usePrevious";
import { useEffect, useRef } from "react";
import { ArrowRight } from "react-feather";
import { transitions } from "theme/styles";
import {
  Flex,
  TouchableArea,
  WebBottomSheet,
  styled,
  useMedia,
  useScrollbarStyles,
  useShadowPropsMedium,
  useSporeColors
} from "ui/src";
import { zIndexes } from "ui/src/theme";
import Trace from "features/telemetry/Trace";
const DRAWER_SPECS = {
  WIDTH_XL: "390px",
  WIDTH: "320px",
  MARGIN: "8px",
  OFFSET: "10px"
};
export const MODAL_WIDTH = "368px";
const AccountDrawerScrollWrapper = styled(Flex, {
  "$platform-web": {
    overflowY: "auto",
    overflowX: "hidden",
    overscrollBehavior: "contain"
  },
  borderRadius: "$rounded20"
});
const Container = styled(Flex, {
  height: "100%",
  "$platform-web": { position: "fixed" },
  top: DRAWER_SPECS.MARGIN,
  right: "0",
  zIndex: zIndexes.dropdown
});
const sharedContainerStyles = {
  borderRadius: "$rounded12",
  backgroundColor: "$surface1",
  borderWidth: "$spacing1",
  borderColor: "$surface3"
};
const SideDrawerContainer = styled(Flex, {
  ...sharedContainerStyles,
  mr: `-${DRAWER_SPECS.WIDTH_XL}`,
  transition: `margin-right ${transitions.duration.medium}`,
  width: DRAWER_SPECS.WIDTH_XL,
  maxWidth: DRAWER_SPECS.WIDTH_XL,
  $xl: {
    mr: `-${DRAWER_SPECS.WIDTH}`,
    width: DRAWER_SPECS.WIDTH,
    maxWidth: DRAWER_SPECS.WIDTH
  },
  variants: {
    open: {
      true: {
        mr: 8,
        $xl: { mr: 8 }
      }
    }
  }
});
const CloseDrawer = styled(Flex, {
  animation: "fast",
  opacity: 0.6,
  height: "100%",
  p: "$spacing24",
  pl: "$spacing12",
  pr: `calc(18px + ${DRAWER_SPECS.OFFSET})`,
  borderTopLeftRadius: "$rounded20",
  borderBottomLeftRadius: "$rounded20",
  borderTopRightRadius: "$none",
  borderBottomRightRadius: "$none",
  "$group-hover": {
    x: "$spacing8",
    backgroundColor: "rgba(153,161,189,0.08)"
  }
});
function AccountSideDrawer({ isOpen, onClose, children }) {
  const scrollbarStyles = useScrollbarStyles();
  const accountDrawer = useAccountDrawer();
  const shadowProps = useShadowPropsMedium();
  const wasAccountDrawerOpen = usePrevious(accountDrawer.isOpen);
  const scrollRef = useRef(null);
  const colors = useSporeColors();
  useEffect(() => {
    if (wasAccountDrawerOpen && !accountDrawer.isOpen) {
      scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [accountDrawer, wasAccountDrawerOpen]);
  return <Flex row height={`calc(100% - 2 * ${DRAWER_SPECS.MARGIN})`}>
      {isOpen && <Trace logPress eventOnTrigger={InterfaceEventName.MINI_PORTFOLIO_TOGGLED} properties={{ type: "close" }}>
          <TouchableArea group zIndex={zIndexes.background} width={60}>
            <CloseDrawer onPress={onClose} data-testid="close-account-drawer">
              <ArrowRight color={colors.neutral2.val} size={28} />
            </CloseDrawer>
          </TouchableArea>
        </Trace>}
      <SideDrawerContainer open={isOpen} {...shadowProps}>
        {
    /* id used for child InfiniteScrolls to reference when it has reached the bottom of the component */
  }
        <AccountDrawerScrollWrapper
    ref={scrollRef}
    style={scrollbarStyles}
    id="wallet-dropdown-scroll-wrapper"
    borderRadius={10}
    height="100%"
  >
          {children}
        </AccountDrawerScrollWrapper>
      </SideDrawerContainer>
    </Flex>;
}
function Drawer({ children }) {
  const accountDrawer = useAccountDrawer();
  const media = useMedia();
  if (media.md) {
    return <WebBottomSheet
      className="account-drawer-mobile"
      data-testid="account-drawer"
      isOpen={accountDrawer.isOpen}
      onClose={accountDrawer.close}
    >
        {children}
      </WebBottomSheet>;
  }
  return <Container className="account-drawer" data-testid="account-drawer">
      <AccountSideDrawer isOpen={accountDrawer.isOpen} onClose={accountDrawer.close}>
        {children}
      </AccountSideDrawer>
    </Container>;
}
function AccountDrawer() {
  const accountDrawer = useAccountDrawer();
  useEffect(() => {
    const escapeKeyDownHandler = (event) => {
      if (event.key === "Escape" && accountDrawer.isOpen) {
        event.preventDefault();
        accountDrawer.close();
      }
    };
    document.addEventListener("keydown", escapeKeyDownHandler);
    return () => {
      document.removeEventListener("keydown", escapeKeyDownHandler);
    };
  }, [accountDrawer]);
  useDisableScrolling(accountDrawer.isOpen);
  return <Drawer>
      <DefaultMenu />
    </Drawer>;
}
export default AccountDrawer;
